import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as api from "../../services/api";
import { exportProductsToExcel, importProductsFromExcel } from "../../utils/excelUtils";
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from "../../utils/imageUtils";
import "./ProductManagement.css";

export default function ProductManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    category: "",
    subcategory: "",
    images: "",
    variants: []
  });
  const [variantForm, setVariantForm] = useState({
    sku: "",
    sizeLabel: "",
    price: "",
    mrp: "",
    stock: ""
  });
  const [sizeLabelSearch, setSizeLabelSearch] = useState("");
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [error, setError] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [selectedCategorySubcategories, setSelectedCategorySubcategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [variantEditForm, setVariantEditForm] = useState({
    sku: "",
    sizeLabel: "",
    price: "",
    mrp: "",
    stock: ""
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");
  const [filterHasVariants, setFilterHasVariants] = useState("all");
  const [filterStock, setFilterStock] = useState("all");

  useEffect(() => {
    loadData();
    // If on /admin/products/new, open the add modal
    if (location.pathname === '/admin/products/new') {
      setShowAddModal(true);
    }
  }, [location.pathname]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData, unitsData] = await Promise.all([
        api.getProducts(),
        api.getCategories(),
        api.getUnits()
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
      setUnits(unitsData);
      setFilteredUnits(unitsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'title') {
      // Auto-generate slug from title if slug hasn't been manually edited
      setFormData(prev => ({
        ...prev,
        title: value,
        slug: slugManuallyEdited ? prev.slug : generateSlug(value)
      }));
    } else if (name === 'slug') {
      // Mark slug as manually edited if user changes it
      setSlugManuallyEdited(true);
      setFormData(prev => ({
        ...prev,
        slug: value
      }));
    } else if (name === 'category') {
      // When category changes, load its subcategories and reset subcategory
      const selectedCategory = categories.find(cat => cat._id === value);
      setSelectedCategorySubcategories(selectedCategory?.subcategories || []);
      setFormData(prev => ({
        ...prev,
        category: value,
        subcategory: "" // Reset subcategory when category changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Auto-generate SKU from title and size label
  const generateSKU = (title, sizeLabel) => {
    if (!title || !sizeLabel) return "";
    
    // Get first character of each word from title (uppercase)
    const titleChars = title
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
    
    // Clean size label (remove spaces, special chars, keep alphanumeric)
    const cleanSizeLabel = sizeLabel.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    // Combine: title initials + size label
    return `${titleChars}-${cleanSizeLabel}`;
  };

  const handleSizeLabelSearch = (value) => {
    setSizeLabelSearch(value);
    setVariantForm(prev => ({ ...prev, sizeLabel: value }));
    
    if (value.trim()) {
      const filtered = units.filter(unit =>
        unit.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredUnits(filtered);
      setShowUnitDropdown(true);
    } else {
      setFilteredUnits(units);
      setShowUnitDropdown(true);
    }
  };

  const handleSelectUnit = (unitName) => {
    setVariantForm(prev => {
      const newForm = { ...prev, sizeLabel: unitName };
      // Auto-generate SKU when size label is selected
      if (formData.title && unitName) {
        newForm.sku = generateSKU(formData.title, unitName);
      }
      return newForm;
    });
    setSizeLabelSearch(unitName);
    setShowUnitDropdown(false);
  };

  const handleAddVariant = () => {
    if (!variantForm.sizeLabel || !variantForm.price) {
      alert('Please fill Size Label and Price');
      return;
    }

    // Auto-generate SKU if not provided
    let sku = variantForm.sku;
    if (!sku && formData.title && variantForm.sizeLabel) {
      sku = generateSKU(formData.title, variantForm.sizeLabel);
    }

    if (!sku) {
      alert('Please provide Size Label to auto-generate SKU');
      return;
    }

    const newVariant = {
      sku: sku,
      sizeLabel: variantForm.sizeLabel,
      price: parseFloat(variantForm.price),
      mrp: variantForm.mrp ? parseFloat(variantForm.mrp) : undefined,
      stock: parseInt(variantForm.stock) || 0
    };

    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));

    setVariantForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
    setSizeLabelSearch("");
    setShowUnitDropdown(false);
  };

  const handleRemoveVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleEditVariant = (index) => {
    const variant = formData.variants[index];
    setEditingVariantIndex(index);
    setVariantEditForm({
      sku: variant.sku || "",
      sizeLabel: variant.sizeLabel || "",
      price: variant.price || "",
      mrp: variant.mrp || "",
      stock: variant.stock || ""
    });
  };

  const handleSaveVariantEdit = () => {
    if (!variantEditForm.sizeLabel || !variantEditForm.price) {
      alert('Please fill Size Label and Price');
      return;
    }

    const updatedVariant = {
      sku: variantEditForm.sku,
      sizeLabel: variantEditForm.sizeLabel,
      price: parseFloat(variantEditForm.price),
      mrp: variantEditForm.mrp ? parseFloat(variantEditForm.mrp) : undefined,
      stock: parseInt(variantEditForm.stock) || 0
    };

    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === editingVariantIndex ? updatedVariant : v)
    }));

    setEditingVariantIndex(null);
    setVariantEditForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
  };

  const handleCancelVariantEdit = () => {
    setEditingVariantIndex(null);
    setVariantEditForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
  };

  // Helper function to get category name
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'N/A';
    const category = categories.find(cat => cat._id === categoryId);
    return category?.name || 'N/A';
  };

  // Compress image if it's larger than 100KB
  const compressImage = (file, maxSizeKB = 100) => {
    return new Promise((resolve, reject) => {
      if (file.size <= maxSizeKB * 1024) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read image file'));
      
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions to keep under size limit
          // Start with more aggressive resizing for very large images
          let maxDimension = 1200;
          if (file.size > 500 * 1024) {
            maxDimension = 800; // More aggressive for very large files
          } else if (file.size > 300 * 1024) {
            maxDimension = 1000;
          }
          
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            } else {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Use better image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try different quality levels to get under size limit
          let quality = 0.85; // Start with lower quality for better compression
          let attempts = 0;
          const maxAttempts = 10;
          
          const tryCompress = () => {
            attempts++;
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              if (blob.size <= maxSizeKB * 1024 || quality <= 0.1 || attempts >= maxAttempts) {
                // If still too large after compression, reduce dimensions further
                if (blob.size > maxSizeKB * 1024 && maxDimension > 400) {
                  maxDimension = Math.max(400, maxDimension - 200);
                  width = img.width > img.height ? maxDimension : (img.width * maxDimension) / img.height;
                  height = img.height > img.width ? maxDimension : (img.height * maxDimension) / img.width;
                  canvas.width = width;
                  canvas.height = height;
                  ctx.drawImage(img, 0, 0, width, height);
                  quality = 0.7;
                  attempts = 0;
                  tryCompress();
                  return;
                }
                resolve(blob);
              } else {
                quality = Math.max(0.1, quality - 0.1);
                tryCompress();
              }
            }, file.type || 'image/jpeg', quality);
          };
          tryCompress();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      setUploadError(`Only image files are allowed: ${invalidFiles.map(f => f.name).join(', ')}`);
      setTimeout(() => setUploadError(''), 5000);
      e.target.value = '';
      return;
    }

    setUploadingImages(true);
    setUploadError('');
    setUploadSuccess(false);
    setImageUploadProgress(`Processing ${files.length} image(s)...`);

    try {
      const uploadedUrls = [];
      const failedFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const originalSize = (file.size / 1024).toFixed(2);
        setImageUploadProgress(`Processing ${i + 1}/${files.length}: ${file.name} (${originalSize}KB)...`);
        
        try {
          // Compress image if it's larger than 100KB
          let fileToUpload = file;
          if (file.size > 100 * 1024) {
            setImageUploadProgress(`Compressing ${i + 1}/${files.length}: ${file.name}...`);
            try {
              fileToUpload = await compressImage(file, 100);
              const compressedSize = (fileToUpload.size / 1024).toFixed(2);
              if (fileToUpload.size > 100 * 1024) {
                setImageUploadProgress(`Warning: ${file.name} is still ${compressedSize}KB after compression. Uploading anyway...`);
              } else {
                setImageUploadProgress(`Compressed ${file.name}: ${originalSize}KB → ${compressedSize}KB`);
              }
            } catch (compressError) {
              console.error(`Compression failed for ${file.name}:`, compressError);
              setImageUploadProgress(`Compression failed for ${file.name}, uploading original...`);
              // Continue with original file if compression fails
            }
          }
          
          setImageUploadProgress(`Uploading ${i + 1}/${files.length}: ${file.name}...`);
          
          // Ensure we have a File object (compressed might be a Blob)
          if (fileToUpload instanceof Blob && !(fileToUpload instanceof File)) {
            fileToUpload = new File([fileToUpload], file.name, { type: file.type || 'image/jpeg' });
          }
          
          const result = await api.uploadImage(fileToUpload);
          
          if (result && result.url) {
            // Validate the URL - base64 URLs should have the format: data:image/type;base64,<data>
            if (result.url.startsWith('data:image/')) {
              if (!result.url.includes(';base64,')) {
                console.error('Invalid base64 URL format:', result.url.substring(0, 100));
                failedFiles.push({ name: file.name, error: 'Invalid image data format' });
                continue;
              }
              // Check if there's actual data after the comma
              const base64Data = result.url.split(';base64,')[1];
              if (!base64Data || base64Data.length < 10) {
                console.error('Base64 data is missing or too short');
                failedFiles.push({ name: file.name, error: 'Image data is incomplete' });
                continue;
              }
            }
            uploadedUrls.push(result.url);
            console.log(`Successfully added URL for ${file.name}, length: ${result.url.length}`);
          } else {
            console.error('Invalid upload response:', result);
            failedFiles.push({ name: file.name, error: 'Invalid response from server' });
          }
        } catch (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          let errorMsg = uploadError.response?.data?.message || uploadError.message || 'Upload failed';
          
          // Handle specific "request entity too large" errors
          if (errorMsg.includes('request entity too large') || 
              errorMsg.includes('PayloadTooLargeError') ||
              errorMsg.includes('413') ||
              uploadError.response?.status === 413) {
            errorMsg = `Image is too large (${originalSize}KB). Please compress the image to under 100KB and try again.`;
          }
          
          failedFiles.push({ name: file.name, error: errorMsg });
        }
      }

      if (uploadedUrls.length > 0) {
        // Add uploaded URLs to existing images
        // Use a special separator for base64 URLs to avoid breaking them
        const IMAGE_SEPARATOR = ' ||| ';
        const existingImages = formData.images ? formData.images.split(IMAGE_SEPARATOR).map(url => url.trim()).filter(url => url) : [];
        const allImages = [...existingImages, ...uploadedUrls];
        const imagesString = allImages.join(IMAGE_SEPARATOR);
        console.log('Setting images:', imagesString.substring(0, 200)); // Debug log (truncated)
        setFormData(prev => ({
          ...prev,
          images: imagesString
        }));
        
        if (failedFiles.length > 0) {
          setImageUploadProgress(`✅ Uploaded ${uploadedUrls.length} image(s), ❌ Failed: ${failedFiles.length}`);
          setUploadError(`Some images failed to upload:\n${failedFiles.map(f => `${f.name}: ${f.error}`).join('\n')}`);
          setTimeout(() => {
            setImageUploadProgress('');
            setUploadError('');
          }, 5000);
        } else {
          setImageUploadProgress(`✅ Successfully uploaded ${uploadedUrls.length} image(s)`);
          setUploadSuccess(true);
          setTimeout(() => {
            setImageUploadProgress('');
            setUploadSuccess(false);
          }, 3000);
        }
      } else {
        if (failedFiles.length > 0) {
          setImageUploadProgress('❌ All images failed to upload');
          setUploadError(`Failed to upload images:\n${failedFiles.map(f => `${f.name}: ${f.error}`).join('\n')}`);
          setTimeout(() => {
            setImageUploadProgress('');
            setUploadError('');
          }, 5000);
        } else {
          setImageUploadProgress('');
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setImageUploadProgress('❌ Failed to upload images');
      setUploadError('Failed to upload images. Please try again.');
      setTimeout(() => {
        setImageUploadProgress('');
        setUploadError('');
      }, 5000);
    } finally {
      setUploadingImages(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUploadError("");
    setUploadSuccess(false);

    if (!formData.title || !formData.category) {
      setError("Please fill in all required fields (Title, Category)");
      return;
    }
    
    // If no variants, price is required
    if (formData.variants.length === 0 && !formData.price) {
      setError("Please add at least one variant with price, or provide a product price");
      return;
    }
    
    // If variants exist, ensure at least one has price
    if (formData.variants.length > 0) {
      const hasValidVariant = formData.variants.some(v => v.price && v.price > 0);
      if (!hasValidVariant) {
        setError("Please add at least one variant with a valid price");
        return;
      }
    }

    setUploading(true);
    setImageUploadProgress(editingProduct ? 'Updating product...' : 'Creating product...');

    try {
      const productData = {
        title: formData.title,
        slug: formData.slug || generateSlug(formData.title),
        description: formData.description || "",
        category: formData.category,
        subcategory: formData.subcategory || "",
        // Use first variant price as product price if no main price, or use main price if provided
        price: formData.price ? parseFloat(formData.price) : (formData.variants.length > 0 ? parseFloat(formData.variants[0].price) : 0),
        // Use first variant MRP as product MRP if no main MRP, or use main MRP if provided
        mrp: formData.mrp ? parseFloat(formData.mrp) : (formData.variants.length > 0 && formData.variants[0].mrp ? parseFloat(formData.variants[0].mrp) : undefined),
        images: formData.images ? formData.images.split(' ||| ').map(url => url.trim()).filter(url => url) : [],
        variants: formData.variants.length > 0 ? formData.variants : undefined
      };

      if (editingProduct) {
        // Update product
        setImageUploadProgress('Updating product...');
        await api.updateProduct(editingProduct._id, productData);
        setImageUploadProgress('✅ Product updated successfully!');
      } else {
        // Create product using API
        setImageUploadProgress('Creating product...');
        await api.createProduct(productData);
        setImageUploadProgress('✅ Product created successfully!');
      }

      setUploadSuccess(true);
      
      // Reset form and reload after a short delay
      setTimeout(() => {
        setFormData({
          title: "",
          slug: "",
          description: "",
          category: "",
          subcategory: "",
          images: "",
          variants: []
        });
        setEditingProduct(null);
        setShowAddModal(false);
        setSizeLabelSearch("");
        setShowUnitDropdown(false);
        setEditingVariantIndex(null);
        setVariantEditForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
        setImageUploadProgress('');
        setUploadSuccess(false);
        loadData();
      }, 1500);
    } catch (error) {
      console.error('Error saving product:', error);
      let errorMsg = error.response?.data?.message || error.message || 'Failed to save product';
      
      // Handle specific "request entity too large" errors
      if (errorMsg.includes('request entity too large') || 
          errorMsg.includes('PayloadTooLargeError') ||
          errorMsg.includes('413') ||
          error.response?.status === 413) {
        const imageCount = formData.images ? formData.images.split(' ||| ').filter(url => url.trim()).length : 0;
        errorMsg = `Product data is too large (likely due to ${imageCount} large image(s)). Please compress images to under 100KB each before uploading.`;
      }
      
      setError(errorMsg);
      setImageUploadProgress(`❌ ${errorMsg}`);
      setUploadError(errorMsg);
      setTimeout(() => {
        setImageUploadProgress('');
        setUploadError('');
      }, 8000); // Show error longer for important messages
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const categoryId = product.category?._id || product.category || "";
    const selectedCategory = categories.find(cat => cat._id === categoryId);
    setSelectedCategorySubcategories(selectedCategory?.subcategories || []);
    setSlugManuallyEdited(false); // Reset slug edit flag
    
    // Map variants to ensure sizeLabel is set (backend might send 'label' instead)
    const mappedVariants = (product.variants || []).map(v => ({
      sku: v.sku || "",
      sizeLabel: v.sizeLabel || v.label || "",
      price: v.price || 0,
      mrp: v.mrp || undefined,
      stock: v.stock || 0
    }));
    
    setFormData({
      title: product.title || product.name || "",
      slug: product.slug || generateSlug(product.title || product.name || ""),
      description: product.description || "",
      category: categoryId,
      subcategory: product.subcategory || "",
      images: product.images ? (Array.isArray(product.images) ? product.images.join(' ||| ') : product.images) : "",
      variants: mappedVariants
    });
    setShowAddModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await api.deleteProduct(productId);
      loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert(error.message || 'Failed to delete product');
    }
  };

  const handleDownloadExcel = () => {
    try {
      exportProductsToExcel(products, categories);
      alert('Products exported to Excel successfully!');
    } catch (error) {
      console.error('Error exporting products:', error);
      alert('Failed to export products to Excel');
    }
  };

  const handleUploadExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setError("");

    try {
      const importedProducts = await importProductsFromExcel(file, categories);
      
      if (importedProducts.length === 0) {
        alert('No valid products found in the Excel file');
        setUploading(false);
        return;
      }

      // Ask for confirmation
      const confirmed = window.confirm(
        `Found ${importedProducts.length} products. Do you want to import them?`
      );

      if (!confirmed) {
        setUploading(false);
        return;
      }

      // Import products one by one
      let successCount = 0;
      let errorCount = 0;

      for (const product of importedProducts) {
        try {
          await api.createProduct(product);
            successCount++;
        } catch (error) {
          console.error('Error importing product:', error);
          errorCount++;
        }
      }

      alert(`Import completed! ${successCount} products imported successfully, ${errorCount} failed.`);
      loadData();
    } catch (error) {
      console.error('Error importing products:', error);
      setError(error.message || 'Failed to import products from Excel');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Filter and search products
  const filteredProducts = products.filter(product => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const titleMatch = (product.title || product.name || "").toLowerCase().includes(query);
      const descriptionMatch = (product.description || "").toLowerCase().includes(query);
      const skuMatch = product.variants?.some(v => v.sku?.toLowerCase().includes(query));
      if (!titleMatch && !descriptionMatch && !skuMatch) {
        return false;
      }
    }

    // Category filter
    if (filterCategory) {
      const categoryId = product.category?._id || product.category;
      if (categoryId !== filterCategory) {
        return false;
      }
    }

    // Price range filter
    if (filterPriceMin && product.price < parseFloat(filterPriceMin)) {
      return false;
    }
    if (filterPriceMax && product.price > parseFloat(filterPriceMax)) {
      return false;
    }

    // Variants filter
    if (filterHasVariants === "yes" && (!product.variants || product.variants.length === 0)) {
      return false;
    }
    if (filterHasVariants === "no" && product.variants && product.variants.length > 0) {
      return false;
    }

    // Stock filter
    if (filterStock === "in_stock") {
      const hasStock = product.variants?.some(v => (v.stock || 0) > 0) || false;
      if (!hasStock) return false;
    }
    if (filterStock === "out_of_stock") {
      const hasStock = product.variants?.some(v => (v.stock || 0) > 0) || false;
      if (hasStock) return false;
    }

    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterCategory("");
    setFilterPriceMin("");
    setFilterPriceMax("");
    setFilterHasVariants("all");
    setFilterStock("all");
  };

  if (loading) {
    return <div className="product-management-loading">Loading products...</div>;
  }

  return (
    <div className="product-management">
      <div className="product-management-header">
        <h1>Product Management</h1>
        <div className="product-actions">
          <label className="btn-upload" htmlFor="product-upload">
            {uploading ? '⏳ Uploading...' : '📥 Upload Excel'}
            <input
              id="product-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUploadExcel}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
          <button 
            className="btn-download"
            onClick={handleDownloadExcel}
            disabled={products.length === 0}
          >
            📥 Download Excel
          </button>
          <button 
            className="btn-primary"
            onClick={() => {
              setEditingProduct(null);
              setSlugManuallyEdited(false);
              setSelectedCategorySubcategories([]);
              setFormData({
                title: "",
                slug: "",
                description: "",
                category: "",
                subcategory: "",
                images: "",
                variants: []
              });
              setShowAddModal(true);
            }}
          >
            ➕ Add New Product
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search by title, description, or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filters-row">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat._id} value={cat._id}>
                {cat.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Min Price (₹)"
            value={filterPriceMin}
            onChange={(e) => setFilterPriceMin(e.target.value)}
            className="filter-input"
          />
          <input
            type="number"
            placeholder="Max Price (₹)"
            value={filterPriceMax}
            onChange={(e) => setFilterPriceMax(e.target.value)}
            className="filter-input"
          />
          <select
            value={filterHasVariants}
            onChange={(e) => setFilterHasVariants(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Products</option>
            <option value="yes">With Variants</option>
            <option value="no">Without Variants</option>
          </select>
          <select
            value={filterStock}
            onChange={(e) => setFilterStock(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Stock Status</option>
            <option value="in_stock">In Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>
          <button
            onClick={clearFilters}
            className="btn-clear-filters"
            title="Clear all filters"
          >
            🗑️ Clear Filters
          </button>
        </div>
        <div className="results-count">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Title</th>
              <th>Category</th>
              <th>Price</th>
              <th>MRP</th>
              <th>Variants</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  {products.length === 0 
                    ? "No products found. Add your first product!" 
                    : "No products match your search/filter criteria."}
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => (
                <tr key={product._id}>
                  <td>
                    <img 
                      src={product.images?.[0] ? convertGoogleDriveUrlForDisplay(product.images[0]) : getPlaceholderImage(50, 50, 'No Image')} 
                      alt={product.title || product.name}
                      className="product-thumb"
                      onError={(e) => {
                        // Prevent infinite retry loops
                        if (e.target.dataset.retryCount) {
                          e.target.src = getPlaceholderImage(50, 50, 'No Image');
                          e.target.onerror = null; // Stop retrying
                          return;
                        }
                        e.target.dataset.retryCount = '1';
                        
                        const originalSrc = e.target.src;
                        
                        // Skip problematic URLs
                        if (originalSrc.includes('lh3.googleusercontent.com') || 
                            originalSrc.includes('drive.google.com/uc?export=download')) {
                          e.target.src = getPlaceholderImage(50, 50, 'No Image');
                          e.target.onerror = null;
                          return;
                        }
                        
                        // Try thumbnail method if view fails
                        if (originalSrc.includes('drive.google.com')) {
                          const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                            originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                          if (fileIdMatch && fileIdMatch[1] && !originalSrc.includes('thumbnail')) {
                            e.target.src = `https://drive.google.com/thumbnail?id=${fileIdMatch[1]}&sz=w200`;
                            return;
                          }
                        }
                        e.target.src = getPlaceholderImage(50, 50, 'No Image');
                        e.target.onerror = null; // Stop retrying after first attempt
                      }}
                    />
                  </td>
                  <td>{product.title || product.name}</td>
                  <td>{getCategoryName(product.category)}</td>
                  <td>₹{product.price}</td>
                  <td>{product.mrp ? `₹${product.mrp}` : '-'}</td>
                  <td>{product.variants?.length || 0}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEdit(product)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(product._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="products-mobile-view">
        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            {products.length === 0 
              ? "No products found. Add your first product!" 
              : "No products match your search/filter criteria."}
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div key={product._id} className="product-mobile-card">
              <div className="product-mobile-card-header">
                <img 
                  src={product.images?.[0] ? convertGoogleDriveUrlForDisplay(product.images[0]) : getPlaceholderImage(60, 60, 'No Image')} 
                  alt={product.title || product.name}
                  className="product-mobile-card-image"
                  onError={(e) => {
                    // Prevent infinite retry loops
                    if (e.target.dataset.retryCount) {
                      e.target.src = getPlaceholderImage(60, 60, 'No Image');
                      e.target.onerror = null; // Stop retrying
                      return;
                    }
                    e.target.dataset.retryCount = '1';
                    
                    const originalSrc = e.target.src;
                    
                    // Skip problematic URLs
                    if (originalSrc.includes('lh3.googleusercontent.com') || 
                        originalSrc.includes('drive.google.com/uc?export=download')) {
                      e.target.src = getPlaceholderImage(60, 60, 'No Image');
                      e.target.onerror = null;
                      return;
                    }
                    
                    // Try thumbnail method if view fails
                    if (originalSrc.includes('drive.google.com')) {
                      const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                        originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                      if (fileIdMatch && fileIdMatch[1] && !originalSrc.includes('thumbnail')) {
                        e.target.src = `https://drive.google.com/thumbnail?id=${fileIdMatch[1]}&sz=w200`;
                        return;
                      }
                    }
                    e.target.src = getPlaceholderImage(60, 60, 'No Image');
                    e.target.onerror = null; // Stop retrying
                  }}
                />
                <div className="product-mobile-card-title">
                  {product.title || product.name}
                </div>
              </div>
              <div className="product-mobile-card-info">
                <div className="product-mobile-card-info-item">
                  <span className="product-mobile-card-info-label">Category</span>
                  <span className="product-mobile-card-info-value">
                    {getCategoryName(product.category)}
                  </span>
                </div>
                <div className="product-mobile-card-info-item">
                  <span className="product-mobile-card-info-label">Price</span>
                  <span className="product-mobile-card-info-value">₹{product.price}</span>
                </div>
                <div className="product-mobile-card-info-item">
                  <span className="product-mobile-card-info-label">MRP</span>
                  <span className="product-mobile-card-info-value">
                    {product.mrp ? `₹${product.mrp}` : '-'}
                  </span>
                </div>
                <div className="product-mobile-card-info-item">
                  <span className="product-mobile-card-info-label">Variants</span>
                  <span className="product-mobile-card-info-value">
                    {product.variants?.length || 0}
                  </span>
                </div>
              </div>
              <div className="product-mobile-card-actions">
                <button 
                  className="btn-edit"
                  onClick={() => handleEdit(product)}
                >
                  Edit
                </button>
                <button 
                  className="btn-delete"
                  onClick={() => handleDelete(product._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setEditingProduct(null);
          setSlugManuallyEdited(false);
          setSelectedCategorySubcategories([]);
          setSizeLabelSearch("");
          setShowUnitDropdown(false);
          setEditingVariantIndex(null);
          setVariantEditForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingProduct(null);
                  setSlugManuallyEdited(false);
                  setSelectedCategorySubcategories([]);
                  setEditingVariantIndex(null);
                  setVariantEditForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              {error && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  color: '#c62828',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{fontSize: '1.2rem'}}>❌</span>
                  <span>{error}</span>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label style={{color: 'black'}}>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{color: 'black'}}>Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    placeholder="Auto-generated from title"
                  />
                </div>
              </div>

              <div className="form-group">
                <label style={{color: 'black'}}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label style={{color: 'black'}}>Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{color: 'black'}}>Subcategory</label>
                  {selectedCategorySubcategories.length > 0 ? (
                    <select
                      name="subcategory"
                      value={formData.subcategory}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Subcategory</option>
                      {selectedCategorySubcategories.map((subcat) => (
                        <option key={subcat.slug || subcat.name} value={subcat.name}>
                          {subcat.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="subcategory"
                      value={formData.subcategory}
                      onChange={handleInputChange}
                      placeholder="Enter subcategory or select a category first"
                    />
                  )}
                </div>
              </div>


              <div className="form-group">
                <label style={{color: 'black'}}>Images</label>
                
                {/* File Upload Section */}
                <div style={{marginBottom: '1rem', padding: '1rem', border: '2px dashed #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'black'}}>
                    📤 Upload Images (Max 200KB per image)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      cursor: uploadingImages ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {/* Upload Progress and Alerts */}
                  {uploadingImages && imageUploadProgress && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #2196f3',
                      borderRadius: '4px',
                      color: '#1976d2',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{fontSize: '1.2rem'}}>⏳</span>
                      <span>{imageUploadProgress}</span>
                    </div>
                  )}
                  {uploadSuccess && !uploadingImages && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      backgroundColor: '#e8f5e9',
                      border: '1px solid #4caf50',
                      borderRadius: '4px',
                      color: '#2e7d32',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{fontSize: '1.2rem'}}>✅</span>
                      <span>Images uploaded successfully!</span>
                    </div>
                  )}
                  {uploadError && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      backgroundColor: '#ffebee',
                      border: '1px solid #f44336',
                      borderRadius: '4px',
                      color: '#c62828',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      whiteSpace: 'pre-line'
                    }}>
                      <span style={{fontSize: '1.2rem'}}>❌</span>
                      <span>{uploadError}</span>
                    </div>
                  )}
                  <small style={{color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem'}}>
                    ⚠️ Large images (&gt;100KB) will be automatically compressed. Maximum: 200KB per image. Supported formats: JPG, PNG, GIF, WebP
                  </small>
                </div>

                {/* URL Input Section */}
                <div style={{marginTop: '1rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'black'}}>
                    🔗 Or Paste Image URLs
                  </label>
                <input
                  type="text"
                  name="images"
                  value={formData.images}
                  onChange={handleInputChange}
                    placeholder="Paste Google Drive share links or direct image URLs (comma-separated)"
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
                  />
                  <small style={{color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem'}}>
                    💡 <strong>Google Drive:</strong> Right-click image → Get link → Set to "Anyone with the link" → Paste link here<br/>
                    💡 <strong>Multiple images:</strong> Separate URLs with commas
                  </small>
                </div>

                {/* Image Preview */}
                {(() => {
                  // Use the same separator to split images, with backward compatibility for comma-separated
                  const IMAGE_SEPARATOR = ' ||| ';
                  let imageUrls = [];
                  if (formData.images) {
                    // Try the new separator first
                    if (formData.images.includes(IMAGE_SEPARATOR)) {
                      imageUrls = formData.images.split(IMAGE_SEPARATOR).map(url => url.trim()).filter(url => url);
                    } else {
                      // Backward compatibility: if it contains base64 URLs, don't split by comma
                      // Check if it's a single base64 URL
                      if (formData.images.trim().startsWith('data:image/')) {
                        imageUrls = [formData.images.trim()];
                      } else {
                        // Old format: comma-separated (but not base64)
                        imageUrls = formData.images.split(',').map(url => url.trim()).filter(url => url);
                      }
                    }
                  }
                  console.log('Preview imageUrls count:', imageUrls.length); // Debug log
                  if (imageUrls.length > 0) {
                    console.log('First image URL preview:', imageUrls[0].substring(0, 100)); // Debug log
                  }
                  return imageUrls.length > 0 ? (
                  <div style={{marginTop: '1rem'}}>
                    <strong style={{color: 'black', fontSize: '0.9rem'}}>Image Preview ({imageUrls.length} image(s)):</strong>
                    <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap'}}>
                      {imageUrls.map((url, idx) => {
                        const trimmedUrl = url.trim();
                        // For base64 data URLs, use them directly; for other URLs, convert Google Drive URLs
                        let previewUrl;
                        if (trimmedUrl.startsWith('data:image/')) {
                          // Base64 data URL - use directly
                          previewUrl = trimmedUrl;
                        } else {
                          // Regular URL - convert Google Drive URL if needed
                          previewUrl = convertGoogleDriveUrlForDisplay(trimmedUrl);
                        }
                        return (
                          <div key={`preview-${idx}-${trimmedUrl.substring(0, 20)}`} style={{position: 'relative', border: '1px solid #ddd', padding: '0.25rem', borderRadius: '4px', backgroundColor: 'white'}}>
                            <img 
                              src={previewUrl} 
                              alt={`Preview ${idx + 1}`}
                              style={{width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', display: 'block'}}
                              onError={(e) => {
                                // Prevent infinite retry loops
                                if (e.target.dataset.retryCount) {
                                  e.target.src = getPlaceholderImage(80, 80, 'Error');
                                  e.target.onerror = null; // Stop retrying
                                  return;
                                }
                                e.target.dataset.retryCount = '1';
                                
                                // Don't try to fix base64 data URLs - if they fail, show error
                                const originalSrc = e.target.src;
                                if (originalSrc.startsWith('data:image/')) {
                                  e.target.src = getPlaceholderImage(80, 80, 'Error');
                                  e.target.onerror = null;
                                  return;
                                }
                                
                                // Skip problematic URLs
                                if (originalSrc.includes('lh3.googleusercontent.com') || 
                                    originalSrc.includes('drive.google.com/uc?export=download')) {
                                  e.target.src = getPlaceholderImage(80, 80, 'Error');
                                  e.target.onerror = null;
                                  return;
                                }
                                
                                // Try thumbnail method if view fails (only for Google Drive URLs)
                                if (originalSrc.includes('drive.google.com')) {
                                  const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                                    originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                                  if (fileIdMatch && fileIdMatch[1] && !originalSrc.includes('thumbnail')) {
                                    e.target.src = `https://drive.google.com/thumbnail?id=${fileIdMatch[1]}&sz=w200`;
                                    return;
                                  }
                                }
                                e.target.src = getPlaceholderImage(80, 80, 'Error');
                                e.target.onerror = null; // Stop retrying
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updatedImages = imageUrls.filter((_, i) => i !== idx);
                                setFormData(prev => ({ ...prev, images: updatedImages.join(' ||| ') }));
                              }}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                background: 'red',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10
                              }}
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  ) : null;
                })()}
              </div>

              <div className="variants-section">
                <h3>Product Variants</h3>
                <div className="variant-form">
                  {/* Row 1: Size Label and SKU */}
                  <div className="size-label-autocomplete variant-field">
                    <label>Size Label *</label>
                    <input
                      type="text"
                      placeholder="Size Label (Type to search units)"
                      value={sizeLabelSearch || variantForm.sizeLabel}
                      onChange={(e) => {
                        handleSizeLabelSearch(e.target.value);
                        // Auto-generate SKU when size label changes
                        if (formData.title && e.target.value) {
                          const autoSKU = generateSKU(formData.title, e.target.value);
                          setVariantForm(prev => ({ ...prev, sku: autoSKU }));
                        }
                      }}
                      onFocus={() => {
                        setShowUnitDropdown(true);
                        setFilteredUnits(units);
                      }}
                      onBlur={() => {
                        // Delay to allow click on dropdown item
                        setTimeout(() => setShowUnitDropdown(false), 200);
                      }}
                    />
                    {showUnitDropdown && filteredUnits.length > 0 && (
                      <div className="unit-dropdown">
                        {filteredUnits.slice(0, 10).map((unit) => (
                          <div
                            key={unit._id}
                            className="unit-dropdown-item"
                            onClick={() => handleSelectUnit(unit.name)}
                          >
                            {unit.name}
                          </div>
                        ))}
                        {filteredUnits.length > 10 && (
                          <div className="unit-dropdown-more">
                            +{filteredUnits.length - 10} more units
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="sku-input-wrapper variant-field">
                    <label>SKU (Auto-generated)</label>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                      <input
                        type="text"
                        placeholder="Auto-generated from title + size"
                        value={variantForm.sku}
                        onChange={(e) => setVariantForm({...variantForm, sku: e.target.value})}
                        readOnly={!!(formData.title && variantForm.sizeLabel)}
                        style={{flex: 1}}
                      />
                      <span 
                        className="info-icon" 
                        title="SKU is auto-generated from product title and size label. You can edit it manually if needed."
                      >
                        ℹ️
                      </span>
                    </div>
                  </div>
                  
                  {/* Row 2: Price, MRP, and Stock */}
                  <div className="variant-field">
                    <label>Price (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={variantForm.price}
                      onChange={(e) => setVariantForm({...variantForm, price: e.target.value})}
                    />
                  </div>
                  <div className="variant-field">
                    <label>MRP (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="MRP"
                      value={variantForm.mrp}
                      onChange={(e) => setVariantForm({...variantForm, mrp: e.target.value})}
                    />
                  </div>
                  <div className="variant-field">
                    <label>Stock</label>
                    <input
                      type="number"
                      placeholder="Stock"
                      value={variantForm.stock}
                      onChange={(e) => setVariantForm({...variantForm, stock: e.target.value})}
                    />
                  </div>
                  
                  {/* Row 3: Add Button */}
                  <button type="button" onClick={handleAddVariant} className="btn-add-variant">
                    Add Variant
                  </button>
                </div>

                {formData.variants.length > 0 && (
                  <div className="variants-list">
                    {formData.variants.map((variant, index) => (
                      <div key={index} className="variant-item">
                        {editingVariantIndex === index ? (
                          <div className="variant-edit-form">
                            {/* <div className="variant-edit-form-grid"> */}
                            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                              <div className="variant-edit-form-field">
                                <label>Size Label</label>
                                <input
                                  type="text"
                                  value={variantEditForm.sizeLabel}
                                  onChange={(e) => setVariantEditForm({...variantEditForm, sizeLabel: e.target.value})}
                                />
                              </div>
                              <div className="variant-edit-form-field">
                                <label>SKU</label>
                                <input
                                  type="text"
                                  value={variantEditForm.sku}
                                  onChange={(e) => setVariantEditForm({...variantEditForm, sku: e.target.value})}
                                />
                              </div>
                              </div>
                              <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                              <div className="variant-edit-form-field">
                                <label>Price (₹) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={variantEditForm.price}
                                  onChange={(e) => setVariantEditForm({...variantEditForm, price: e.target.value})}
                                />
                              </div>
                              <div className="variant-edit-form-field">
                                <label>MRP (₹)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={variantEditForm.mrp}
                                  onChange={(e) => setVariantEditForm({...variantEditForm, mrp: e.target.value})}
                                />
                              </div>
                              </div>
                              <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                              <div className="variant-edit-form-field">
                                <label>Stock *</label>
                                <input
                                  type="number"
                                  value={variantEditForm.stock}
                                  onChange={(e) => setVariantEditForm({...variantEditForm, stock: e.target.value})}
                                />
                              </div>
                              </div>
                            {/* </div> */}
                            <div className="variant-edit-form-actions">
                              <button 
                                type="button"
                                onClick={handleCancelVariantEdit}
                                className="variant-edit-cancel-btn"
                              >
                                Cancel
                              </button>
                              <button 
                                type="button"
                                onClick={handleSaveVariantEdit}
                                className="variant-edit-save-btn"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span>
                              {variant.sizeLabel} - ₹{variant.price}
                              {variant.mrp && variant.mrp > variant.price && (
                                <span style={{textDecoration: 'line-through', color: '#666', marginLeft: '0.5rem'}}>
                                  ₹{variant.mrp}
                                </span>
                              )}
                              {' '}(SKU: {variant.sku}, Stock: {variant.stock})
                            </span>
                            <div style={{display: 'flex', gap: '0.5rem'}}>
                              <button 
                                type="button"
                                onClick={() => handleEditVariant(index)}
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  background: '#667eea',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Edit
                              </button>
                              <button 
                                type="button"
                                onClick={() => handleRemoveVariant(index)}
                                className="btn-remove-variant"
                              >
                                Remove
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Submission Progress */}
              {uploading && imageUploadProgress && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: '4px',
                  color: '#1976d2',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{fontSize: '1.2rem'}}>⏳</span>
                  <span>{imageUploadProgress}</span>
                </div>
              )}
              {uploadSuccess && !uploading && imageUploadProgress && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #4caf50',
                  borderRadius: '4px',
                  color: '#2e7d32',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{fontSize: '1.2rem'}}>✅</span>
                  <span>{imageUploadProgress}</span>
                </div>
              )}
              {uploadError && !uploading && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  color: '#c62828',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{fontSize: '1.2rem'}}>❌</span>
                  <span>{uploadError}</span>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowAddModal(false);
                  setEditingProduct(null);
                  setSlugManuallyEdited(false);
                  setSelectedCategorySubcategories([]);
                  setEditingVariantIndex(null);
                  setVariantEditForm({ sku: "", sizeLabel: "", price: "", mrp: "", stock: "" });
                }} className="btn-cancel" disabled={uploading}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <span style={{marginRight: '0.5rem'}}>⏳</span>
                      {editingProduct ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingProduct ? 'Update Product' : 'Create Product'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

